import {
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ExpandedState,
  type SortingState,
  type Updater,
} from '@tanstack/react-table';
import { useState } from 'react';
import { Pagination } from '@avanta/shared/components/Pagination';
import { SortableTableHeader } from '@avanta/shared/components/SortableTableHeader';
import { TableEmptyContent } from '@avanta/shared/components/TableEmptyContent';
import { TableSkeleton } from '@avanta/shared/components/TableSkeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@avanta/shared/components/ui/Table';
import { ExpandableTableRow } from './ExpandableTableRow';
import { type ExpandableTableProps } from './types';

const DEFAULT_PAGE_SIZE = 10;

export const ExpandableTable = <T,>({
  data,
  columns,
  getRowId,
  renderExpandedContent,
  initialSorting = [],
  isLoading = false,
  emptyTitle,
  emptyMessage,
  tableClassName,
  expandAriaLabel,
  collapseAriaLabel,
  pageSize = DEFAULT_PAGE_SIZE,
}: ExpandableTableProps<T>): React.ReactElement => {
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize,
  });
  const columnCount = columns.length + 1;

  const handleSortingChange = (updater: Updater<SortingState>): void => {
    setSorting(updater);
    setPagination(previous =>
      previous.pageIndex === 0 ? previous : { ...previous, pageIndex: 0 }
    );
  };

  const table = useReactTable({
    data,
    columns,
    getRowId,
    defaultColumn: {
      enableSorting: false,
    },
    state: {
      sorting,
      expanded,
      pagination,
    },
    onSortingChange: handleSortingChange,
    onExpandedChange: setExpanded,
    onPaginationChange: setPagination,
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const rows = table.getRowModel().rows;

  return (
    <div className="flex flex-col gap-3">
      <Table className={tableClassName}>
        <TableHeader>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <TableHead key={header.id}>
                  <SortableTableHeader header={header} />
                </TableHead>
              ))}
              <TableHead className="w-10" />
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableSkeleton columns={columnCount} />
          ) : rows.length > 0 ? (
            rows.map(row => (
              <ExpandableTableRow
                key={row.id}
                row={row}
                renderExpandedContent={renderExpandedContent}
                expandAriaLabel={expandAriaLabel}
                collapseAriaLabel={collapseAriaLabel}
              />
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columnCount} className="border-none">
                {(emptyMessage || emptyTitle) && (
                  <TableEmptyContent emptyTitle={emptyTitle} emptyMessage={emptyMessage} />
                )}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {!isLoading && <Pagination table={table} />}
    </div>
  );
};
